import { useState } from 'react';
import { useLang } from 'hooks/lang';

import { Column, Tabs } from 'components/Blocks';

import { SingleService } from './SingleService';
import { TotalRow } from './TotalRow';

import { useProjectProfile } from '../context';

import { calculateCountryTotal, setCostsWithInitialValues } from './utils';
import { getFirst } from 'utils/array';

import { PhaseProps } from './types';
import { getCountryLabel } from 'utils/countries';

export function Services({ state, ...props }: PhaseProps) {
  const { t } = useLang();
  const { project } = useProjectProfile();

  const [country, setCountry] = useState(getFirst(project.countries));

  const [openControl, setOpenControl] = useState<Record<string, boolean>>({});

  const servicesByCountry = project.services.map(
    ({ costCalculation, costs, id, name, service, outsourceConfig, outsourced }) => ({
      costCalculation,
      costs: costs[country],
      id,
      name,
      service,
      outsourceConfig,
      outsourced,
    }),
  );

  const actualServicesByCountry = state.servicesActualMetrics.map(
    ({ costCalculation, costs, id, name, service, outsourceConfig, outsourced }) => ({
      costCalculation,
      costs: costs[country],
      id,
      name,
      service,
      outsourceConfig,
      outsourced,
    }),
  );

  const countryTabs = project.countries.map((singleCountry) => ({
    value: singleCountry,

    getLabel: () => {
      const countryMissingFilling = state.servicesActualMetrics.some(({ costs }) =>
        costs[singleCountry].some(({ value }) => !value),
      );

      return `${getCountryLabel(singleCountry)}${countryMissingFilling ? '*' : ''}`;
    },
  }));

  return (
    <>
      <Tabs
        tabs={countryTabs}
        value={country}
        onTabChange={setCountry}
        type="highlighted"
        responsiveBreakpoint="PHONE"
      />

      <Column gap="5">
        {servicesByCountry.map((service, index) => (
          <SingleService
            // using just the service is not enough, as this could potently trigger changes on inputs
            key={`${service.id}#${country}`}
            service={service}
            actualService={actualServicesByCountry[index]}
            country={country}
            isOpen={openControl[`${service.id}#${country}`]}
            toggleOpen={() =>
              setOpenControl((old) => ({
                ...old,
                [`${service.id}#${country}`]: !old[`${service.id}#${country}`],
              }))
            }
            {...props}
          />
        ))}

        <TotalRow
          title={t('total-country')}
          initialValue={calculateCountryTotal(servicesByCountry.map(setCostsWithInitialValues))}
          currentValue={calculateCountryTotal(actualServicesByCountry)}
          invoiceValue={calculateCountryTotal(servicesByCountry)}
        />
      </Column>
    </>
  );
}
